import React, { useEffect, useState, useCallback, useRef } from "react";
import { FaPlus, FaTimes, FaSpinner } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useData } from "../../context/DataContext";
import { SipDropdown, SipSearch, SipTextarea } from "../form/SipControls";
function AddInvLab({
  openModal,
  isModalOpen,
  closeModal,
  form,
  setForm,
  postInvLab,
  isEditing,
}) {
  // State untuk fitur pencarian inventaris
  const [searchKode, setSearchKode] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [errors, setErrors] = useState({});

  // Refs untuk debouncing dan tracking
  const searchTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const { lab, getLabs, inventaris = [], getInventaris } = useData();

  // Effect untuk load data awal ketika modal dibuka
  useEffect(() => {
    if (isModalOpen) {
      getLabs();
      // Load inventaris dengan parameter kosong untuk mendapatkan semua data hanya sekali
      if (getInventaris && !hasInitializedRef.current) {
        hasInitializedRef.current = true;
        getInventaris("").catch((error) => {
          console.error("Error loading initial inventaris:", error);
        });
      }
    } else {
      // Reset flag ketika modal ditutup
      hasInitializedRef.current = false;
      // Reset search states ketika modal ditutup
      setSearchKode("");
      setSearchResults([]);
      setShowSearchResults(false);
      setErrors({});
    }
  }, [isModalOpen, getInventaris, getLabs]);
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: -50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.34, 1.56, 0.64, 1], // Custom spring easing
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: -50,
      transition: {
        duration: 0.3,
        ease: "easeIn",
      },
    },
  };
  const headerVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { delay: 0.2, duration: 0.3 },
    },
  };

  const closeButtonVariants = {
    hidden: { opacity: 0, rotate: -90 },
    visible: {
      opacity: 1,
      rotate: 0,
      transition: { delay: 0.2, duration: 0.3 },
    },
    hover: {
      rotate: 90,
      transition: { duration: 0.2 },
    },
  };

  // Fungsi untuk mencari inventaris berdasarkan kode
  const searchInventaris = useCallback(
    async (kode) => {
      if (!kode.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      try {
        setLoadingSearch(true);
        await getInventaris(kode);
      } catch (error) {
        console.error("Error searching inventaris:", error);
        setSearchResults([]);
        setShowSearchResults(true); // Tetap tampilkan untuk menunjukkan pesan tidak ditemukan
      } finally {
        setLoadingSearch(false);
      }
    },
    [getInventaris]
  );

  // Handler untuk perubahan input pencarian
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchKode(value);

      // Jika input kosong, sembunyikan hasil pencarian
      if (!value.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce search - tunggu 500ms setelah user berhenti mengetik
      searchTimeoutRef.current = setTimeout(() => {
        searchInventaris(value);
      }, 500);
    },
    [searchInventaris]
  );

  // Handler untuk memilih inventaris dari hasil pencarian
  const setHandleInventaris = (selectedInventaris) => {
    if (!selectedInventaris || !selectedInventaris.id) {
      console.error("Invalid inventaris data:", selectedInventaris);
      return;
    }

    // Set form dengan data inventaris yang dipilih
    setForm((prev) => ({
      ...prev,
      inventaris_id: selectedInventaris.id,
      aset:
        selectedInventaris.desc ||
        selectedInventaris.kode ||
        "Tidak ada deskripsi",
    }));

    // Update search kode dengan kode yang dipilih
    setSearchKode(selectedInventaris.kode || "");
    setShowSearchResults(false);

    // Clear error jika ada
    if (errors.inventaris_id) {
      setErrors((prev) => ({
        ...prev,
        inventaris_id: "",
      }));
    }
  };

  // Update search results ketika inventaris dari context berubah
  useEffect(() => {
    if (
      inventaris &&
      Array.isArray(inventaris) &&
      searchKode.trim() &&
      !loadingSearch
    ) {
      setSearchResults(inventaris);
      setShowSearchResults(true);
    } else if (!searchKode.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [inventaris, searchKode, loadingSearch]);

  // Cleanup timeout saat component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".search-container")) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchResults]);

  const handleSubmit = (e) => {
    e.preventDefault();
    postInvLab();
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <>
      <button
        onClick={() => openModal()}
        className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-200"
      >
        <FaPlus />
        <span className="font-medium">Tambah Inventaris</span>
      </button>

      {/* ==================== ADD/EDIT MODAL ==================== */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeModal}
            style={{ backdropFilter: "blur(4px)" }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <motion.h3
                    className="text-xl font-semibold text-gray-900"
                    variants={headerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {isEditing
                      ? "Edit Inventaris Lab"
                      : "Tambah Inventaris Lab"}
                  </motion.h3>
                  <motion.button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
                    variants={closeButtonVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <FaTimes />
                  </motion.button>
                </div>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bagian */}
                  {/* Pencarian Inventaris */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cari Inventaris berdasarkan Kode{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative search-container">
                      <div className="relative">
                        <SipSearch
                          value={searchKode}
                          onChange={handleSearchChange}
                          placeholder="Masukkan kode inventaris untuk mencari..."
                        />
                        {loadingSearch && (
                          <FaSpinner className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
                        )}
                      </div>

                      {/* Hasil Pencarian */}
                      {showSearchResults &&
                        searchResults &&
                        searchResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            {searchResults.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => setHandleInventaris(item)}
                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-200"
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {item.kode || "N/A"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {item.desc || "Tidak ada deskripsi"}
                                    </p>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Klik untuk pilih
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                      {/* Pesan jika tidak ada hasil */}
                      {showSearchResults &&
                        (!searchResults || searchResults.length === 0) &&
                        searchKode &&
                        !loadingSearch && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg p-4">
                            <p className="text-gray-500 text-center">
                              Tidak ada inventaris ditemukan dengan kode "
                              {searchKode}"
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Tampilkan inventaris yang dipilih */}
                    {form.inventaris_id && form.aset && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          <span className="font-medium">
                            Inventaris Dipilih:
                          </span>{" "}
                          {searchKode} - {form.aset}
                        </p>
                      </div>
                    )}

                    {errors.inventaris_id && (
                      <p className="text-red-500 text-sm mt-1 animate__animated animate__shakeX">
                        {errors.inventaris_id}
                      </p>
                    )}
                  </div>

                  {/* Kondisi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kondisi *
                    </label>
                    <SipDropdown
                      name="kondisi"
                      value={form.kondisi}
                      onChange={handleInputChange}
                      placeholder="Pilih Kondisi"
                      filter={false}
                      options={[
                        { label: "Baik", value: "Baik" },
                        { label: "Rusak", value: "Rusak" },
                      ]}
                    />
                  </div>

                  {/* Lab */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lab *
                    </label>
                    <SipDropdown
                      name="lab_id"
                      value={form.lab_id ? String(form.lab_id) : ""}
                      onChange={handleInputChange}
                      placeholder="Pilih Lab"
                      options={(lab || []).map((labItem) => ({
                        label: labItem.nama_lab,
                        value: String(labItem.id),
                      }))}
                    />
                  </div>

                  {/* Tindakan */}
                </div>

                {/* Keterangan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keterangan
                  </label>
                  <SipTextarea
                    name="ket"
                    value={form.ket}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Keterangan tambahan..."
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                  <motion.button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Batal
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:shadow-lg transition-all duration-200"
                    whileHover={{ y: -1, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isEditing ? "Update" : "Simpan"}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AddInvLab;
